import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Badge,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Form,
  Image,
  Modal,
  Radio,
  Tag,
  Tooltip,
} from 'antd';
import {
  debounce,
  filter,
  forEach,
  isEmpty,
  map,
  toInteger,
  uniq,
} from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Countdown from 'react-countdown';
import { useHistory, useLocation } from 'react-router-dom';
import MappedUnitsCell from '../../../../../components/MappedUnitsCell';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as CommentIcon } from '../../../../../assets/svg/comment-dots-solid.svg';
import { ReactComponent as FilterIcon } from '../../../../../assets/svg/sliders-solid.svg';
import ORDER_TAG_BULK_ACTIONS, {
  BREACH_TIME_CRITICAL_THRESHOLD,
  BREACH_TIME_WARNING_THRESHOLD,
  CANCELLATION_TYPE,
  CRITICAL_ORDERS_KEY,
  DATE_FORMATS,
  DELIVERY_TYPE,
  DELIVERY_TYPE_OPTIONS,
  ORDER_TYPE_FILTER_OPTIONS,
  EXPRESS_DELIVERY_EMOJI,
  EXPRESS_DELIVERY_TOOLTIP_TEXT,
  FULFILLMENT_STATUS,
  LIMIT,
  ORDER_STATUS,
  ORDER_TAG_TYPES,
  ORDER_TYPE,
  ORDERS_POLLING_INTERVAL,
  QUICK_DELIVERY_EMOJI,
  QUICK_DELIVERY_TOOLTIP_TEXT,
  ROUTES,
  SORT,
  SORTING,
  STOCK_COLUMN_TOOLTIPS,
  TABLE_FILTER_KEYS,
  TRANSFER_ORDER_STATUS,
} from '../../../../../common/constants';
import {
  formatDate,
  getRowClassName,
  handleOpenUserDrawer,
  hasNonEmptyValueObj,
  renderer,
  shouldNotifyForNewOrders,
  toDayjsRange,
  humanizeEnum,
} from '../../../../../common/utils';
import ColumnTitleWithInfo from '../../../../../components/ColumnTitleWithInfo';
import CommonPreview from '../../../../../components/CommonPreview';
import CommonPreviewTable from '../../../../../components/CommonPreviewTable';
import CommonTable from '../../../../../components/CommonTable';
import LoaderComponent from '../../../../../components/LoaderComponent';
import BulkActionButton from '../../../../../components/BulkActionButton';
import OrderDynamicTags from '../../../../../components/OrderDynamicTags';
import OrderStatusSelect from '../../../../../components/OrderStatusSelect';
import SearchComponent from '../../../../../components/SearchComponent';
import ShowDelayedOrdersSwitch from '../../../../../components/ShowDelayedOrdersSwitch';
import SelectComponent from '../../../../../components/SelectComponent';
import UnitScanModal from '../../../../../components/UnitScan/UnitScanModal';
import { UNIT_SCAN_MODES } from '../../../../../components/UnitScan/constants';
import SortDropdown from '../../../../../components/SortDropdown';
import { CITIES, PINCODES } from '../../../../../components/graphql/Queries';
import useInfiniteScroll from '../../../../../hooks/useInfiniteScroll';
import useOrderAlert from '../../../../../hooks/useOrderAlert';
import useRetainedTableState from '../../../../../hooks/useRetainedTableFilterState';
import AddCommentModal from '../../../../../libraryModules/orders/modules/all/components/AddCommentModal';
import AddDelayReasonModal from '../../../../../libraryModules/orders/modules/all/components/AddDelayReasonModal';
import AllFilterOrderModal from '../../../../../libraryModules/orders/modules/all/components/AllFilterOrderModal';
import OrderTagsFilter from '../../../../../components/OrderTagsFilter';
import AllOrderItemModal from '../../../../../libraryModules/orders/modules/all/components/AllOrderItemModal';
import AllOrderList from '../../../../../libraryModules/orders/modules/all/components/AllOrderList';
import { ORDERS_SELECT } from '../../../../../libraryModules/orders/modules/all/graphql/Queries';
import ContentMasterModal from '../../all/components/ContentMasterModal';
import DelayReasonModal from '../../all/components/DelayReasonModal';
import NoteModal from '../../all/components/NoteModal';
import { CANCEL_ORDER_ADMIN } from '../../all/graphql/Mutations';
import { ORDER_ITEMS_ADMIN } from '../../all/graphql/Queries';
import {
  UPDATE_ORDER_METADATA,
  UPDATE_ORDER_STATUS_ADMIN,
  UPDATE_ORDER_STATUS_ADMIN_V2,
} from '../graphql/Mutations';
import {
  LIBRARIES,
  FULFILMENT_POSSIBILITY_ORDERS_ADMIN,
} from '../graphql/Queries';
import FulfilmentPossibilityDrawer from '../../../../../components/FulfilmentPossibilityDrawer';
import BigToyIcon from './BigToyIcon';
import StatusFlag from './OrderTagRenderer';
import RenderCopyIcon from '../../../../../components/RenderCopyIcon';
import { isUnitsFeatureEnabledForLibrary } from '../../../../../hooks/useInventoryUnitsFeature';
import useToyDrawer from '../../../../../hooks/useToyDrawer';
import createOrderItemStaffColumns from '../../../../../components/OrderItemStaff/createOrderItemStaffColumns';
import { patchOrderItemStaff } from '../../../../../components/OrderItemStaff/utils';
import BulkAssignStaffButton from '../../../../../components/OrderItemStaff/BulkAssignStaffButton';

const { RangePicker } = DatePicker;
const { Group } = Radio;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialNewDeliveryFilter = {
  limit: LIMIT,
  search: '',
  skip: 0,
  showPlacedOrderCount: true,
  showConfirmedOrderCount: true,
};

const initialNewDeliverySort = {
  field: 'expectedDeliveryDate',
  order: SORT?.ASC,
};

const initialTableFilter = {
  skip: 0,
  limit: LIMIT,
  search: '',
};

const initialTableSort = {
  field: 'createdAt',
  order: 'DESC',
};

let filterScrollDebounceJob = null;

const sort = [
  {
    label: SORTING?.ORDER_ID_A_Z?.label,
    value: SORTING?.ORDER_ID_A_Z?.value,
    field: SORTING?.ORDER_ID_A_Z?.field,
    sort: SORTING?.ORDER_ID_A_Z?.sort,
  },
  {
    label: SORTING?.ORDER_ID_Z_A?.label,
    value: SORTING?.ORDER_ID_Z_A?.value,
    field: SORTING?.ORDER_ID_Z_A?.field,
    sort: SORTING?.ORDER_ID_Z_A?.sort,
  },
  {
    label: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.label,
    value: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.value,
    field: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.field,
    sort: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.sort,
  },
  {
    label: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.label,
    value: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.value,
    field: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.field,
    sort: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.sort,
  },
];

const NewFulfilmentTable = ({ exportFilter, setExportFilter }) => {
  const location = useLocation();
  const [form] = Form.useForm();
  const [delayForm] = Form.useForm();
  const {
    dispatch,
    state: {
      isDesktop,
      orderCount,
      commonPermissions: {
        isUserPermission,
        isToyPermission,
        isLibraryPermission,
      },
    },
  } = useContext(AppContext);
  const {
    hydrated,
    retainedState,
    saveRetainedFilterState,
  } = useRetainedTableState();

  const {
    playNotificationSound,
    playNotificationSoundForNewOrder,
  } = useOrderAlert();
  const history = useHistory();
  const { openToyDrawer } = useToyDrawer();
  const queryParams = new URLSearchParams(location?.search);
  const orderId = useRef(queryParams?.get('orderId') || '');
  const [orderList, setOrderList] = useState([]);
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const orderSearchTermRef = useRef(orderSearchTerm);
  const [isEmptyOrderList, setIsEmptyOrderList] = useState(false);
  const [order, setOrder] = useState({});
  const [orderItem, setOrderItem] = useState({});
  const [sortedInfo, setSortedInfo] = useState({
    field: 'expectedDeliveryDate',
    columnKey: 'expectedDeliveryDate',
    order: 'ascend',
  });
  const sortedInfoRef = useRef({
    field: 'expectedDeliveryDate',
    columnKey: 'expectedDeliveryDate',
    order: 'ascend',
  });
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const paginationPropRef = useRef(initialPaginationValue);
  const [isOrderLoading, setIsOrderLoading] = useState(true);
  const [isNotePrompts, setIsNotePrompts] = useState(false);
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState(false);
  const [isAddCommentLoading, setIsAddCommentLoading] = useState(false);
  const [isOrderAction, setIsOrderAction] = useState(false);
  const [isFulfillLoading, setIsFulfillLoading] = useState(false);
  const [isFulfillPrompts, setIsFulfillPrompts] = useState(false);
  const [isUnitScanOpen, setIsUnitScanOpen] = useState(false);
  const [isReadyLoading, setIsReadyLoading] = useState(false);
  const [isReadyPrompts, setIsReadyPrompts] = useState(false);
  const [isDelayReasonPrompts, setIsDelayReasonPrompts] = useState(false);
  const [isConfirmPrompts, setIsConfirmPrompts] = useState(false);
  const [isReasonPrompt, setIsReasonPrompt] = useState(false);

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [nestedData, setNestedData] = useState({});
  const getStaffColumns = createOrderItemStaffColumns({
    onReassigned: (...args) =>
      setOrderList((prev) => patchOrderItemStaff(prev, ...args)),
  });
  const [isLoading, setIsLoading] = useState({});

  const [filterSearch, setFilterSearch] = useState('');
  const [filterList, setFilterList] = useState([]);
  const [whereFilter, setWhereFilter] = useState();
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterTags, setFilterTags] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterIndex, setFilterIndex] = useState(null);
  const [finalWhereFilters, setFinalWhereFilters] = useState();
  const finalWhereFiltersRef = useRef(null);
  const [finalFilterTags, setFinalFilterTags] = useState(null);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const selectedTagIdsRef = useRef([]);
  const [filterIsEnd, setFilterIsEnd] = useState(false);
  const [isEmptyFilterList, setIsEmptyFilterList] = useState(false);
  const [visible, setVisible] = useState(false);
  const [toyImages, setToyImages] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [toyPreviewImage, setToyPreviewImage] = useState();
  const [isContentMasterModalOpen, setIsContentMasterModalOpen] = useState(
    false,
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedOrdersById, setSelectedOrdersById] = useState({});
  const [isFetchMoreOrderLoading, setIsFetchMoreOrderLoading] = useState(false);
  const [thresholdData, setThresholdData] = useState({});
  const [possibilityDrawerVisible, setPossibilityDrawerVisible] = useState(
    false,
  );
  const [selectedPossibilityItem, setSelectedPossibilityItem] = useState(null);

  const [isAllSortPopoverOpen, setIsAllSortPopoverOpen] = useState(false);
  const [isAllFilterModalOpen, setIsAllFilterModalOpen] = useState(false);
  const [isAllOrderItemModalOpen, setIsAllOrderItemModalOpen] = useState(false);

  const tempComment = useRef(null);
  const tempUnfulfilledReason = useRef(null);
  const [isActive, setIsActive] = useState(true);
  const isPollingRef = useRef(false);
  const previousCountRef = useRef(0);
  const isInitialMountRef = useRef(true);
  const statusFilter = [ORDER_STATUS?.PLACED?.value];
  const [initializedFromRetained, setInitializedFromRetained] = useState(false);

  const tableFilterKey = TABLE_FILTER_KEYS.NEW_FULFILMENT_POSSIBILITY;

  useEffect(() => {
    if (!hydrated) return;
    if (initializedFromRetained) return;

    const retaineFiters = retainedState?.[tableFilterKey];

    if (!retaineFiters) {
      setInitializedFromRetained(true);
      return;
    }
    const baseFilters = retaineFiters?.filters || {};
    const normalizedFilters = {
      ...baseFilters,
      createdAt: toDayjsRange(baseFilters?.createdAt),
      deliveredOn: toDayjsRange(baseFilters?.deliveredOn),
    };
    setFinalWhereFilters(normalizedFilters);
    finalWhereFiltersRef.current = normalizedFilters;
    setFilterTags(retaineFiters?.filterTags || {});
    setFinalFilterTags(retaineFiters?.filterTags || {});
    const savedSort = retaineFiters?.sort;
    if (savedSort?.field === 'createdAt' && savedSort?.order === 'descend') {
      const defaultSortObj = {
        field: 'expectedDeliveryDate',
        columnKey: 'expectedDeliveryDate',
        order: 'ascend',
      };
      setSortedInfo(defaultSortObj);
      sortedInfoRef.current = defaultSortObj;
      saveRetainedFilterState(tableFilterKey, {
        sort: defaultSortObj,
      });
    } else {
      setSortedInfo(
        savedSort || {
          field: 'expectedDeliveryDate',
          columnKey: 'expectedDeliveryDate',
          order: 'ascend',
        },
      );
      sortedInfoRef.current = savedSort || {
        field: 'expectedDeliveryDate',
        columnKey: 'expectedDeliveryDate',
        order: 'ascend',
      };
    }

    setPaginationProp(retaineFiters?.paginationProp);
    paginationPropRef.current = retaineFiters?.paginationProp;

    setOrderSearchTerm(retaineFiters?.searchTerm);
    orderSearchTermRef.current = retaineFiters?.searchTerm;
    setInitializedFromRetained(true);
  }, [hydrated, initializedFromRetained, retainedState]);

  const handleQueryCompleted = (responseData) => {
    const criticalThreshold = toInteger(
      responseData?.configs?.find(
        (config) => config?.key === BREACH_TIME_CRITICAL_THRESHOLD,
      )?.value,
    );
    const warningThreshold = toInteger(
      responseData?.configs?.find(
        (config) => config?.key === BREACH_TIME_WARNING_THRESHOLD,
      )?.value,
    );
    setThresholdData({
      criticalThreshold,
      warningThreshold,
    });
    dispatch({
      type: 'SET_ORDER_COUNT',
      data: {
        ...orderCount,
        fulfilmentPossibilityOrderCount: responseData?.count,
      },
    });
    const newOrders = responseData?.data || [];
    const existingIds = new Set(
      orderList?.map((currentOrder) => currentOrder?.orderNumber),
    );
    const uniqueNewOrders = newOrders?.filter(
      (currentOrder) => !existingIds?.has(currentOrder.orderNumber),
    );

    const shouldNotify = shouldNotifyForNewOrders(
      uniqueNewOrders,
      orderList,
      isPollingRef,
      isInitialMountRef,
      finalWhereFilters,
      orderSearchTerm,
      previousCountRef,
    );
    if (shouldNotify) {
      playNotificationSoundForNewOrder(
        uniqueNewOrders,
        paginationProp?.current > 1,
      );
    }

    previousCountRef.current = responseData?.count;
    isInitialMountRef.current = false;
    if (isDesktop) {
      setOrderList([...(responseData?.data || [])]);
    } else if (isPollingRef?.current) {
      setOrderList([...uniqueNewOrders, ...orderList]);
      isPollingRef.current = false;
    } else {
      setOrderList([...orderList, ...uniqueNewOrders]);
    }
    if (responseData?.count === 0 && initialPaginationValue?.total === 0) {
      setIsEmptyOrderList(true);
    } else {
      setIsEmptyOrderList(false);
    }

    if ((responseData?.data || [])?.length < LIMIT) {
      setIsActive(false);
    }

    const pagination = {
      ...paginationProp,
      defaultPageSize: LIMIT,
      total: responseData?.count,
    };
    setPaginationProp(pagination);
    paginationPropRef.current = pagination;

    saveRetainedFilterState(tableFilterKey, {
      paginationProp: pagination,
    });

    setIsOrderLoading(false);
    setIsFetchMoreOrderLoading(false);
  };

  const [fulfilmentPossibilityOrdersAdmin] = useLazyQuery(
    FULFILMENT_POSSIBILITY_ORDERS_ADMIN,
    {
      onCompleted: (response) => {
        handleQueryCompleted(response?.fulfilmentPossibilityOrdersAdmin);
      },
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const newOrdersAdminV2 = (options) => {
    fulfilmentPossibilityOrdersAdmin(options);
  };

  const infiniteScrollRef = useInfiniteScroll({
    isFetching: isFetchMoreOrderLoading,
    isActive,
    onLoadMore: () => {
      setIsFetchMoreOrderLoading(true);
      const nextSkip = orderList?.length;
      paginationPropRef.current = {
        ...paginationPropRef?.current,
        skip: nextSkip,
      };
      saveRetainedFilterState(tableFilterKey, {
        paginationProp: {
          ...paginationPropRef?.current,
          skip: nextSkip,
        },
      });
      newOrdersAdminV2({
        variables: {
          filter: {
            ...initialNewDeliveryFilter,
            skip: nextSkip,
            limit: LIMIT,
            orderType: ORDER_TYPE?.NEW,
            orderIds: finalWhereFilters?.orderNumber,
            search: orderSearchTerm,
            deliveryType: finalWhereFilters?.deliveryType,
            orderTypes:
              finalWhereFilters?.orderTypes?.length > 0
                ? finalWhereFilters?.orderTypes
                : undefined,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            deliveredOn: {
              from: finalWhereFilters?.deliveredOn?.from?.$d,
              to: finalWhereFilters?.deliveredOn?.to?.$d,
            },
            ...(finalWhereFilters?.expectedDeliveryDate?.from
              ? {
                  expectedDeliveryDate: {
                    from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                    to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                  },
                }
              : {}),
            pincodeIds: finalWhereFilters?.pincodes,
            libraryIds: finalWhereFilters?.name,
            city: finalWhereFilters?.city,
            isPreOrder: finalWhereFilters?.isPreOrder,
            isDelayed: finalWhereFilters?.isDelayed,
            orderTagIds:
              selectedTagIdsRef?.current?.length > 0
                ? selectedTagIdsRef.current
                : undefined,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    },
    isDesktop,
  });

  const handleSort = (values) => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    paginationPropRef.current = { ...paginationProp, current: 1, skip: 0 };
    setIsOrderLoading(true);
    setIsAllSortPopoverOpen(false);
    setIsActive(true);
    setOrderList([]);
    const filteredSort = sort?.filter((item) => item?.value === values?.sort);
    if (filteredSort?.[0]?.field) {
      setSortedInfo({
        ...sortedInfo,
        field: filteredSort?.[0]?.field,
        columnKey: filteredSort?.[0]?.field,
        order: filteredSort?.[0]?.sort === SORT?.ASC ? 'ascend' : 'descend',
        column: filteredSort?.[0]?.field,
      });
      sortedInfoRef.current = {
        ...sortedInfoRef?.current,
        field: filteredSort?.[0]?.field,
        columnKey: filteredSort?.[0]?.field,
        order: filteredSort?.[0]?.sort === SORT?.ASC ? 'ascend' : 'descend',
        column: filteredSort?.[0]?.field,
      };
    }

    saveRetainedFilterState(tableFilterKey, {
      paginationProp: { ...paginationProp, current: 1, skip: 0 },
      sort: {
        ...sortedInfo,
        field: filteredSort?.[0]?.field,
        columnKey: filteredSort?.[0]?.field,
        order: filteredSort?.[0]?.sort === SORT?.ASC ? 'ascend' : 'descend',
        column: filteredSort?.[0]?.field,
      },
    });
    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          orderIds: finalWhereFilters?.orderNumber,
          search: orderSearchTerm,
          deliveryType: finalWhereFilters?.deliveryType,
          orderTypes:
            finalWhereFilters?.orderTypes?.length > 0
              ? finalWhereFilters?.orderTypes
              : undefined,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : statusFilter,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFilters?.deliveredOn?.from?.$d,
            to: finalWhereFilters?.deliveredOn?.to?.$d,
          },
          ...(finalWhereFilters?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                  to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          pincodeIds: finalWhereFilters?.pincodes,
          libraryIds: finalWhereFilters?.name,
          city: finalWhereFilters?.city,
        },
        sort: filteredSort?.[0]
          ? {
              field: filteredSort?.[0]?.field,
              order: filteredSort?.[0]?.sort,
            }
          : initialNewDeliverySort,
      },
    });
  };

  const [orderItemsAdmin] = useLazyQuery(ORDER_ITEMS_ADMIN, {
    fetchPolicy: 'no-cache',
    onError() {},
  });

  const criticalOrdersRef = useRef(new Set());

  useEffect(() => {
    // eslint-disable-next-line no-undef
    const stored = localStorage.getItem(CRITICAL_ORDERS_KEY);
    if (stored) {
      try {
        criticalOrdersRef.current = new Set(JSON.parse(stored));
      } catch (error) {
        criticalOrdersRef.current = new Set();
      }
    }
  }, []);

  const [pincodes, { loading: isPincodesLoading }] = useLazyQuery(PINCODES, {
    onCompleted: (response) => {
      if (response?.pincodes?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.pincodes?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.pincodes?.data, (item) =>
        optionsCopy?.push({
          label: item?.pincode,
          key: item?.id,
        }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [libraries, { loading: isLibrariesLoading }] = useLazyQuery(LIBRARIES, {
    onCompleted: (response) => {
      if (response?.libraries?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.libraries?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.libraries?.data, (item) =>
        optionsCopy?.push({ label: item?.name, key: item?.id }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [cities, { loading: isCityLoading }] = useLazyQuery(CITIES, {
    onCompleted: (response) => {
      if (response?.cities?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.cities?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.cities?.data, (item) =>
        optionsCopy?.push({ label: item?.name, key: item?.name }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [orderSelect, { loading: isOrderSelectLoading }] = useLazyQuery(
    ORDERS_SELECT,
    {
      onCompleted: (response) => {
        if (response?.newOrdersAdminV2?.data?.length < LIMIT) {
          setFilterIsEnd(true);
        }
        if (response?.newOrdersAdminV2?.count === 0) {
          setIsEmptyFilterList(true);
        } else {
          setIsEmptyFilterList(false);
        }
        const optionsCopy = [];
        forEach(response?.newOrdersAdminV2?.data, (item) =>
          optionsCopy?.push({
            label: item?.orderNumber,
            key: item?.id,
          }),
        );
        setFilterList([...filterList, ...optionsCopy]);
        setFilterLoading(false);
      },
      fetchPolicy: 'network-only',
      onError() {
        setFilterLoading(false);
      },
    },
  );

  const [cancelOrderAdmin] = useMutation(CANCEL_ORDER_ADMIN, {
    onError: () => {},
  });

  const [updateOrderStatusAdmin] = useMutation(UPDATE_ORDER_STATUS_ADMIN, {
    onError: () => {},
  });

  const [updateOrderMetadata] = useMutation(UPDATE_ORDER_METADATA, {
    onError: () => {},
  });

  const [updateOrderStatusAdminV2] = useMutation(UPDATE_ORDER_STATUS_ADMIN_V2, {
    onError: () => {},
  });

  const fetchOrders = () => {
    if (!hydrated || !initializedFromRetained) return;

    if (orderId?.current) {
      setOrderSearchTerm(orderId?.current);
      orderSearchTermRef.current = orderId?.current;

      saveRetainedFilterState(tableFilterKey, {
        searchTerm: orderId?.current,
      });
    }
    isPollingRef.current = true;
    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          skip:
            (paginationPropRef?.current?.current - 1) *
            (paginationPropRef?.current?.pageSize || 0),
          search: orderSearchTermRef?.current,
          limit: paginationPropRef?.current?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          orderIds: finalWhereFiltersRef?.current?.orderNumber,
          deliveryType: finalWhereFiltersRef?.current?.deliveryType,
          orderTypes:
            finalWhereFiltersRef?.current?.orderTypes?.length > 0
              ? finalWhereFiltersRef?.current?.orderTypes
              : undefined,
          status:
            finalWhereFiltersRef?.current?.status?.length > 0
              ? finalWhereFiltersRef?.current?.status
              : statusFilter,
          dateRange: {
            from: finalWhereFiltersRef?.current?.createdAt?.from?.$d,
            to: finalWhereFiltersRef?.current?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFiltersRef?.current?.deliveredOn?.from?.$d,
            to: finalWhereFiltersRef?.current?.deliveredOn?.to?.$d,
          },
          ...(finalWhereFiltersRef?.current?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from:
                    finalWhereFiltersRef?.current?.expectedDeliveryDate?.from
                      ?.$d,
                  to:
                    finalWhereFiltersRef?.current?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          pincodeIds: finalWhereFiltersRef?.current?.pincodes,
          statusTiming: finalWhereFiltersRef?.current?.statusTiming,
          libraryIds: finalWhereFiltersRef?.current?.name,
          city: finalWhereFiltersRef?.current?.city,
          isPreOrder: finalWhereFiltersRef?.current?.isPreOrder,
          isDelayed: finalWhereFiltersRef?.current?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sortedInfoRef?.current?.column
          ? {
              field: sortedInfoRef?.current?.field,
              order:
                sortedInfoRef?.current?.order === 'ascend'
                  ? SORT?.ASC
                  : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  useEffect(() => {
    fetchOrders();
    const intervalId = setInterval(fetchOrders, ORDERS_POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, [hydrated, initializedFromRetained]);

  const handleTagFilterChange = (tagIds) => {
    selectedTagIdsRef.current = tagIds;
    setSelectedTagIds(tagIds);
    setIsOrderLoading(true);
    setOrderList([]);
    setIsActive(true);
    setNestedData({});
    setExpandedRowKeys([]);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    paginationPropRef.current = {
      ...paginationPropRef.current,
      current: 1,
      skip: 0,
    };
    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          orderIds: finalWhereFilters?.orderNumber,
          search: orderSearchTerm,
          deliveryType: finalWhereFilters?.deliveryType,
          orderTypes:
            finalWhereFilters?.orderTypes?.length > 0
              ? finalWhereFilters?.orderTypes
              : undefined,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : statusFilter,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFilters?.deliveredOn?.from?.$d,
            to: finalWhereFilters?.deliveredOn?.to?.$d,
          },
          ...(finalWhereFilters?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                  to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          pincodeIds: finalWhereFilters?.pincodes,
          libraryIds: finalWhereFilters?.name,
          city: finalWhereFilters?.city,
          isPreOrder: finalWhereFilters?.isPreOrder,
          isDelayed: finalWhereFilters?.isDelayed,
          orderTagIds: tagIds?.length > 0 ? tagIds : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
    saveRetainedFilterState(tableFilterKey, {
      selectedTagIds: tagIds,
      paginationProp: { ...paginationProp, current: 1, skip: 0 },
    });
  };

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    paginationPropRef.current = { ...paginationPropRef.current, ...pagination };
    setIsOrderLoading(true);
    setSortedInfo(sorter);
    sortedInfoRef.current = sorter;
    setNestedData({});
    setExpandedRowKeys([]);
    saveRetainedFilterState(tableFilterKey, {
      paginationProp: pagination,
      sort: sorter,
    });
    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          skip,
          limit: pagination?.pageSize,
          orderType: ORDER_TYPE?.NEW,
          orderIds: finalWhereFilters?.orderNumber,
          search: orderSearchTerm,
          deliveryType: finalWhereFilters?.deliveryType,
          orderTypes:
            finalWhereFilters?.orderTypes?.length > 0
              ? finalWhereFilters?.orderTypes
              : undefined,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : statusFilter,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFilters?.deliveredOn?.from?.$d,
            to: finalWhereFilters?.deliveredOn?.to?.$d,
          },
          ...(finalWhereFilters?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                  to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          statusTiming: finalWhereFilters?.statusTiming,
          pincodeIds: finalWhereFilters?.pincodes,
          libraryIds: finalWhereFilters?.name,
          city: finalWhereFilters?.city,
          isPreOrder: finalWhereFilters?.isPreOrder,
          isDelayed: finalWhereFilters?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  const getFilterData = (confirm) => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    paginationPropRef.current = {
      ...paginationPropRef?.current,
      current: 1,
      skip: 0,
    };
    setIsOrderLoading(true);
    setNestedData({});
    setExpandedRowKeys([]);
    setOrderList([]);
    setIsActive(true);
    const updatedFilters = {
      ...finalWhereFilters,
      ...whereFilter,
    };
    setFinalWhereFilters(updatedFilters);
    finalWhereFiltersRef.current = updatedFilters;
    setFinalFilterTags(filterTags);
    saveRetainedFilterState(tableFilterKey, {
      filters: updatedFilters,
      filterTags,
      paginationProp: { ...paginationProp, skip: 0, current: 1 },
    });
    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          orderIds: updatedFilters?.orderNumber,
          search: orderSearchTerm,
          deliveryType: updatedFilters?.deliveryType,
          orderTypes:
            updatedFilters?.orderTypes?.length > 0
              ? updatedFilters?.orderTypes
              : undefined,
          status:
            updatedFilters?.status?.length > 0
              ? updatedFilters?.status
              : statusFilter,
          dateRange: {
            from: updatedFilters?.createdAt?.from?.$d,
            to: updatedFilters?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: updatedFilters?.deliveredOn?.from?.$d,
            to: updatedFilters?.deliveredOn?.to?.$d,
          },
          ...(updatedFilters?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: updatedFilters?.expectedDeliveryDate?.from?.$d,
                  to: updatedFilters?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          statusTiming: updatedFilters?.statusTiming,
          pincodeIds: updatedFilters?.pincodes,
          libraryIds: updatedFilters?.name,
          city: updatedFilters?.city,
          isPreOrder: updatedFilters?.isPreOrder,
          isDelayed: updatedFilters?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
    setExportFilter({
      ...exportFilter,
      orderNumber: updatedFilters?.orderNumber,
      deliveryType: updatedFilters?.deliveryType,
      orderTypes: updatedFilters?.orderTypes,
      status: updatedFilters?.status,
      createdAt: updatedFilters?.createdAt,
      deliveredOn: updatedFilters?.deliveredOn,
      pincodes: updatedFilters?.pincodes,
      name: updatedFilters?.name,
      city: updatedFilters?.city,
    });
    if (confirm) {
      confirm();
    }
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const tagsCopy = {
      ...filterTags,
      [dataIndex]: [],
    };
    const whereFiltersCopy = {
      ...whereFilter,
      [dataIndex]: [],
    };
    setFilterVisible(false);
    setFilterTags({ ...filterTags, ...tagsCopy });
    setFinalFilterTags({ ...filterTags, ...tagsCopy });
    setWhereFilter({ ...whereFilter, ...whereFiltersCopy });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFiltersCopy });
    finalWhereFiltersRef.current = {
      ...finalWhereFilters,
      ...whereFiltersCopy,
    };
    saveRetainedFilterState(tableFilterKey, {
      filters: { ...finalWhereFilters, ...whereFiltersCopy },
      filterTags: { ...filterTags, ...tagsCopy },
      paginationProp: { ...paginationProp, skip: 0, current: 1 },
    });
    setIsOrderLoading(true);
    setNestedData({});
    setExpandedRowKeys([]);
    setOrderList([]);
    setIsActive(true);
    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          orderIds: whereFiltersCopy?.orderNumber,
          search: orderSearchTerm,
          deliveryType: whereFiltersCopy?.deliveryType,
          orderTypes:
            whereFiltersCopy?.orderTypes?.length > 0
              ? whereFiltersCopy?.orderTypes
              : undefined,
          status:
            whereFiltersCopy?.status?.length > 0
              ? whereFiltersCopy?.status
              : statusFilter,
          dateRange: {
            from: whereFiltersCopy?.createdAt?.from?.$d,
            to: whereFiltersCopy?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: whereFiltersCopy?.deliveredOn?.from?.$d,
            to: whereFiltersCopy?.deliveredOn?.to?.$d,
          },
          ...(whereFiltersCopy?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: whereFiltersCopy?.expectedDeliveryDate?.from?.$d,
                  to: whereFiltersCopy?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          statusTiming: whereFiltersCopy?.statusTiming,
          pincodeIds: whereFiltersCopy?.pincodes,
          libraryIds: whereFiltersCopy?.name,
          city: whereFiltersCopy?.city,
          isPreOrder: whereFiltersCopy?.isPreOrder,
          isDelayed: whereFiltersCopy?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    paginationPropRef.current = { ...paginationProp, skip: 0, current: 1 };
    setExportFilter({
      ...exportFilter,
      orderNumber: whereFiltersCopy?.orderNumber,
      deliveryType: whereFiltersCopy?.deliveryType,
      orderTypes: whereFiltersCopy?.orderTypes,
      status: whereFiltersCopy?.status,
      createdAt: whereFiltersCopy?.createdAt,
      deliveredOn: whereFiltersCopy?.deliveredOn,
      pincodes: whereFiltersCopy?.pincodes,
      name: whereFiltersCopy?.name,
      city: whereFiltersCopy?.city,
    });
    clearFilters();
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterList([]);
      setFilterLoading(true);
      setIsEmptyFilterList(false);
      setFilterTags({ ...filterTags, ...finalFilterTags });
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
      setFilterIsEnd(false);
      switch (filterIndex) {
        case 'orderTypes':
          setFilterList(
            map(ORDER_TYPE_FILTER_OPTIONS, (item) => ({
              label: item?.label,
              key: item?.value,
            })),
          );
          setFilterLoading(false);
          break;

        case 'pincodes':
          pincodes({
            variables: {
              filter: initialTableFilter,
              sort: initialTableSort,
            },
          });
          break;

        case 'name':
          libraries({
            variables: {
              filter: initialTableFilter,
              sort: initialTableSort,
            },
          });
          break;

        case 'city':
          cities({
            variables: {
              filter: initialTableFilter,
              sort: initialTableSort,
            },
          });
          break;

        case 'orderNumber':
          orderSelect({
            variables: {
              filter: {
                ...initialTableFilter,
                orderType: ORDER_TYPE?.NEW,
                status: statusFilter,
              },
              sort: initialTableSort,
            },
          });
          break;

        default:
          break;
      }
    }
    if (!filterVisible) {
      setFilterTags(finalFilterTags);
      saveRetainedFilterState(tableFilterKey, {
        filterTags: finalFilterTags,
      });
      setWhereFilter(finalWhereFilters);
      setFilterSearch('');
    }
  }, [filterVisible]);

  const onFilterScroll = (event, dataIndex) => {
    if (dataIndex === 'status' || dataIndex === 'orderTypes') {
      return;
    }
    if (filterScrollDebounceJob) {
      filterScrollDebounceJob?.cancel();
      filterScrollDebounceJob = null;
    }
    if (
      !isPincodesLoading &&
      !isOrderSelectLoading &&
      !isLibrariesLoading &&
      !isCityLoading &&
      !filterIsEnd
    ) {
      const target = event?.target;
      const currentLength = filterList?.length;
      if (
        target?.scrollTop + target?.offsetHeight >=
        target?.scrollHeight - 5
      ) {
        filterScrollDebounceJob = debounce(() => {
          setFilterLoading(true);
          switch (dataIndex) {
            case 'pincodes':
              pincodes({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'name':
              libraries({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'city':
              cities({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'orderNumber':
              orderSelect({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                    orderType: ORDER_TYPE?.NEW,
                    status: statusFilter,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            default:
              break;
          }
        }, 500);
        filterScrollDebounceJob();
      }
    }
  };

  const handleFilterSearch = (value, dataIndex) => {
    setFilterSearch(value);
    setFilterIsEnd(false);
    setFilterLoading(true);
    setFilterList([]);
    switch (dataIndex) {
      case 'pincodes':
        pincodes({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'name':
        libraries({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'city':
        cities({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'orderNumber':
        orderSelect({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
              orderType: ORDER_TYPE?.NEW,
              status: statusFilter,
            },
            sort: initialTableSort,
          },
        });
        break;

      default:
        break;
    }
  };

  const changeFilter = (newFilterObj, dataIndex) => {
    const {
      target: { value },
    } = newFilterObj;

    const filterTagsCopy = { ...filterTags };
    let newTagList;
    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filter(filterTagsCopy?.[dataIndex], (item) => {
          if (item?.label) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    } else {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filterTagsCopy?.[dataIndex]
          ? [...filterTagsCopy?.[dataIndex], value]
          : [value],
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const handleDeleteFilter = (value, dataIndex) => {
    let newTagList;

    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTags,
        [dataIndex]: filter(filterTags?.[dataIndex], (item) => {
          if (item?.key) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const onRangePickerChange = (values, dataIndex) => {
    const filtersCopy = { ...whereFilter };
    if (!values?.length) {
      delete filtersCopy?.[dataIndex];
    } else {
      filtersCopy[dataIndex] = {
        from: values[0]?.startOf('day'),
        to: values?.[1]?.endOf('day'),
      };
    }
    setWhereFilter(filtersCopy);
  };

  const onRadioChange = (value, dataIndex) => {
    const filtersCopy = { ...whereFilter };
    if (!value?.length) {
      delete filtersCopy?.[dataIndex];
    } else {
      filtersCopy[dataIndex] = value;
    }
    setWhereFilter(filtersCopy);
  };

  const filterPopup = (dataIndex, showSearch = true) => ({
    filterDropdown: ({ confirm, clearFilters }) => {
      if (
        dataIndex === 'createdAt' ||
        dataIndex === 'deliveredOn' ||
        dataIndex === 'expectedDeliveryDate'
      ) {
        return (
          <div className="custom-filter-dropdown">
            <RangePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onRangePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.from
                  ? [
                      whereFilter?.[dataIndex]?.from,
                      whereFilter?.[dataIndex]?.to,
                    ]
                  : null
              }
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      if (dataIndex === 'deliveryType') {
        return (
          <div className="custom-filter-dropdown">
            <Group
              options={DELIVERY_TYPE_OPTIONS}
              value={whereFilter?.[dataIndex]}
              onChange={(e) => onRadioChange(e?.target?.value, dataIndex)}
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="custom-filter-dropdown">
          <LoaderComponent
            spinning={filterLoading}
            setHeight={35}
            size="medium"
          >
            {showSearch && (
              <SearchComponent
                className="filter-search"
                id="search-container-id-roles"
                placeholder="Search..."
                name={dataIndex}
                query={filterSearch}
                setQuery={setFilterSearch}
                getData={(value) => handleFilterSearch(value, dataIndex)}
              />
            )}
            {filterTags?.[dataIndex]?.length > 0 && (
              <div className="filter-tags-wrapper">
                {map(filterTags?.[dataIndex], (item) => (
                  <Tag
                    key={item?.key}
                    closable
                    onClose={() => handleDeleteFilter(item, dataIndex)}
                    className="filter-tag"
                  >
                    <span>{item?.label}</span>
                  </Tag>
                ))}
              </div>
            )}
            <div
              className="filter-checkboxes"
              onScroll={(e) => onFilterScroll(e, dataIndex)}
            >
              {filterList?.length > 0
                ? map(filterList, (item) => (
                    <div className="single-checkbox-div" key={item?.key}>
                      <Checkbox
                        value={item}
                        checked={whereFilter?.[dataIndex]?.includes(item?.key)}
                        key={item?.key || item}
                        onChange={(e) => changeFilter(e, dataIndex)}
                        className="single-checkbox"
                      >
                        <span>{item?.label}</span>
                      </Checkbox>
                    </div>
                  ))
                : isEmptyFilterList && (
                    <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
                  )}
            </div>
          </LoaderComponent>
          <Divider className="divider-filter" />
          <div className="d-flex justify-center">
            <Button
              size="small"
              className="reset-button"
              id="roles-filter-reset"
              onClick={() => handleReset(clearFilters, dataIndex, confirm)}
            >
              Reset
            </Button>
            <Button
              size="small"
              className="common-button ok-button"
              id="roles-filter-ok"
              type="primary"
              onClick={() => getFilterData(confirm, dataIndex)}
            >
              Ok
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: () =>
      finalWhereFilters?.[dataIndex]?.length > 0 ||
      !isEmpty(finalWhereFilters?.[dataIndex]) ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (isVisible) => {
      setFilterIndex(dataIndex);
      setFilterVisible(isVisible);
    },
  });

  const handleExpand = async (expanded, record) => {
    setIsLoading({
      [record?.id]: true,
    });
    if (!nestedData?.[record?.id]) {
      try {
        const res = await orderItemsAdmin({
          variables: {
            where: {
              id: record?.id,
            },
          },
        });
        if (res?.data) {
          setNestedData((state) => ({
            ...state,
            [record?.id]: res?.data,
          }));
        }
      } catch (error) {
        return error;
      }
    }
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, record?.id]);
    } else {
      setExpandedRowKeys(expandedRowKeys?.filter((key) => key !== record?.id));
    }
    setIsLoading({
      [record?.id]: false,
    });
  };

  const isLastItem = () => true;

  const updateMainOrderList = (orderIds, commentData) => {
    setOrder((prev) => ({
      ...prev,
      metadata: {
        ...commentData,
      },
    }));

    setOrderList((prev) =>
      prev.map((orders) => {
        if (orders?.id === orderIds) {
          return {
            ...orders,
            metadata: {
              ...commentData,
            },
          };
        }
        return orders;
      }),
    );
  };

  const updateNestedDataLocally = (orderIds, orderItemId, newStatus) => {
    setNestedData((prev) => ({
      ...prev,
      [orderIds]: {
        ...prev?.[orderIds],
        orderItemsAdmin: {
          ...prev?.[orderIds]?.orderItemsAdmin,
          data:
            prev?.[orderIds]?.orderItemsAdmin?.data?.map((item) =>
              item?.id === orderItemId
                ? {
                    ...item,
                    status: newStatus,
                    cancellationData:
                      newStatus === FULFILLMENT_STATUS.UNFULFILLED?.value
                        ? { ...tempUnfulfilledReason.current }
                        : null,
                  }
                : item,
            ) || [],
        },
      },
    }));
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setOrderSearchTerm(trimValue);
    orderId.current = trimValue;
    orderSearchTermRef.current = trimValue;
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    paginationPropRef.current = { ...paginationProp, current: 1, skip: 0 };
    saveRetainedFilterState(tableFilterKey, {
      paginationProp: { ...paginationProp, skip: 0, current: 1 },
      searchTerm: trimValue,
    });

    setIsOrderLoading(true);
    setOrderList([]);
    setNestedData({});
    setExpandedRowKeys([]);
    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          orderIds: finalWhereFilters?.orderNumber,
          search: trimValue,
          deliveryType: finalWhereFilters?.deliveryType,
          orderTypes:
            finalWhereFilters?.orderTypes?.length > 0
              ? finalWhereFilters?.orderTypes
              : undefined,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : statusFilter,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFilters?.deliveredOn?.from?.$d,
            to: finalWhereFilters?.deliveredOn?.to?.$d,
          },
          ...(finalWhereFilters?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                  to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          statusTiming: finalWhereFilters?.statusTiming,
          pincodeIds: finalWhereFilters?.pincodes,
          libraryIds: finalWhereFilters?.name,
          city: finalWhereFilters?.city,
          isPreOrder: finalWhereFilters?.isPreOrder,
          isDelayed: finalWhereFilters?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
    setExportFilter({ ...exportFilter, search: trimValue });
  };

  const handleAddCommentInOrder = async (values) => {
    setIsAddCommentLoading(true);
    const response = await cancelOrderAdmin({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          additionalComment: values?.comment?.trim(),
          reason: values?.reason,
        },
      },
    });
    if (response?.data) {
      setIsOrderLoading(true);
      setIsAddCommentModalOpen(false);
      setNestedData({});
      setExpandedRowKeys([]);
      setOrderList([]);
      form?.resetFields();
      newOrdersAdminV2({
        variables: {
          filter: {
            ...initialNewDeliveryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            orderType: ORDER_TYPE?.NEW,
            orderIds: finalWhereFilters?.orderNumber,
            search: orderSearchTerm,
            deliveryType: finalWhereFilters?.deliveryType,
            orderTypes:
              finalWhereFilters?.orderTypes?.length > 0
                ? finalWhereFilters?.orderTypes
                : undefined,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            deliveredOn: {
              from: finalWhereFilters?.deliveredOn?.from?.$d,
              to: finalWhereFilters?.deliveredOn?.to?.$d,
            },
            ...(finalWhereFilters?.expectedDeliveryDate?.from
              ? {
                  expectedDeliveryDate: {
                    from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                    to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                  },
                }
              : {}),
            statusTiming: finalWhereFilters?.statusTiming,
            pincodeIds: finalWhereFilters?.pincodes,
            libraryIds: finalWhereFilters?.name,
            city: finalWhereFilters?.city,
            isPreOrder: finalWhereFilters?.isPreOrder,
            isDelayed: finalWhereFilters?.isDelayed,
            orderTagIds:
              selectedTagIdsRef?.current?.length > 0
                ? selectedTagIdsRef.current
                : undefined,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
    setIsAddCommentLoading(false);
  };

  const handleAddCommentInOrderItem = async (values) => {
    setIsAddCommentLoading(true);
    const response = await updateOrderStatusAdmin({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          orderItemId: orderItem?.id,
          additionalComment: values?.comment?.trim(),
          reason: values?.reason,
          status: FULFILLMENT_STATUS?.UNFULFILLED?.value,
        },
      },
    });
    if (response?.data) {
      if (isLastItem(order?.id, orderItem?.id)) {
        setIsAllOrderItemModalOpen(false);
        setIsOrderLoading(true);
        setNestedData({});
        setExpandedRowKeys([]);
        setOrderList([]);
        form?.resetFields();
        newOrdersAdminV2({
          variables: {
            filter: {
              ...initialNewDeliveryFilter,
              skip:
                (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
              limit: paginationProp?.pageSize || LIMIT,
              orderType: ORDER_TYPE?.NEW,
              orderIds: finalWhereFilters?.orderNumber,
              search: orderSearchTerm,
              deliveryType: finalWhereFilters?.deliveryType,
              orderTypes:
                finalWhereFilters?.orderTypes?.length > 0
                  ? finalWhereFilters?.orderTypes
                  : undefined,
              status:
                finalWhereFilters?.status?.length > 0
                  ? finalWhereFilters?.status
                  : statusFilter,
              dateRange: {
                from: finalWhereFilters?.createdAt?.from?.$d,
                to: finalWhereFilters?.createdAt?.to?.$d,
              },
              deliveredOn: {
                from: finalWhereFilters?.deliveredOn?.from?.$d,
                to: finalWhereFilters?.deliveredOn?.to?.$d,
              },
              ...(finalWhereFilters?.expectedDeliveryDate?.from
                ? {
                    expectedDeliveryDate: {
                      from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                      to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                    },
                  }
                : {}),
              statusTiming: finalWhereFilters?.statusTiming,
              pincodeIds: finalWhereFilters?.pincodes,
              libraryIds: finalWhereFilters?.name,
              city: finalWhereFilters?.city,
              isPreOrder: finalWhereFilters?.isPreOrder,
              isDelayed: finalWhereFilters?.isDelayed,
              orderTagIds:
                selectedTagIdsRef?.current?.length > 0
                  ? selectedTagIdsRef.current
                  : undefined,
            },
            sort: sortedInfo?.column
              ? {
                  field: sortedInfo?.field,
                  order:
                    sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
                }
              : initialNewDeliverySort,
          },
        });
      } else {
        tempUnfulfilledReason.current = {
          reason: values?.reason,
          additionalComment: values?.comment?.trim(),
          type: CANCELLATION_TYPE?.LIBRARY?.value,
          date: Date(),
        };

        updateNestedDataLocally(
          order.id,
          orderItem.id,
          FULFILLMENT_STATUS?.UNFULFILLED?.value,
        );
      }
    }
    form?.resetFields();
    setIsAddCommentLoading(false);
    setIsAddCommentModalOpen(false);
  };

  const handleFulfillOrderItem = async () => {
    setIsFulfillLoading(true);
    const response = await updateOrderStatusAdmin({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          orderItemId: orderItem?.id,
          status: FULFILLMENT_STATUS?.FULFILLED?.value,
        },
      },
    });
    if (response?.data) {
      if (isLastItem(order?.id, orderItem?.id)) {
        setIsAllOrderItemModalOpen(false);
        setIsOrderLoading(true);
        setIsFulfillPrompts(false);
        setNestedData({});
        setOrderList([]);
        setExpandedRowKeys([]);
        newOrdersAdminV2({
          variables: {
            filter: {
              ...initialNewDeliveryFilter,
              skip:
                (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
              limit: paginationProp?.pageSize || LIMIT,
              orderType: ORDER_TYPE?.NEW,
              orderIds: finalWhereFilters?.orderNumber,
              search: orderSearchTerm,
              deliveryType: finalWhereFilters?.deliveryType,
              orderTypes:
                finalWhereFilters?.orderTypes?.length > 0
                  ? finalWhereFilters?.orderTypes
                  : undefined,
              status:
                finalWhereFilters?.status?.length > 0
                  ? finalWhereFilters?.status
                  : statusFilter,
              dateRange: {
                from: finalWhereFilters?.createdAt?.from?.$d,
                to: finalWhereFilters?.createdAt?.to?.$d,
              },
              deliveredOn: {
                from: finalWhereFilters?.deliveredOn?.from?.$d,
                to: finalWhereFilters?.deliveredOn?.to?.$d,
              },
              ...(finalWhereFilters?.expectedDeliveryDate?.from
                ? {
                    expectedDeliveryDate: {
                      from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                      to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                    },
                  }
                : {}),
              statusTiming: finalWhereFilters?.statusTiming,
              pincodeIds: finalWhereFilters?.pincodes,
              libraryIds: finalWhereFilters?.name,
              city: finalWhereFilters?.city,
              isPreOrder: finalWhereFilters?.isPreOrder,
              isDelayed: finalWhereFilters?.isDelayed,
              orderTagIds:
                selectedTagIdsRef?.current?.length > 0
                  ? selectedTagIdsRef.current
                  : undefined,
            },
            sort: sortedInfo?.column
              ? {
                  field: sortedInfo?.field,
                  order:
                    sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
                }
              : initialNewDeliverySort,
          },
        });
      } else {
        updateNestedDataLocally(
          order.id,
          orderItem.id,
          FULFILLMENT_STATUS?.FULFILLED?.value,
        );

        if (tempComment?.current?.[order?.id])
          updateMainOrderList(order?.id, tempComment?.current?.[order?.id]);
      }
      setIsDelayReasonPrompts(false);
      delayForm?.resetFields();
      setIsReadyLoading(false);
    }
    setIsFulfillLoading(false);
  };

  const handleReadyOrder = async (values) => {
    setIsReadyLoading(true);
    const response = await updateOrderStatusAdminV2({
      variables: {
        where: {
          id: values,
        },
        data: {
          status: ORDER_STATUS?.CONFIRMED?.value,
        },
      },
    });
    if (response?.data) {
      setIsOrderLoading(true);
      setIsReadyPrompts(false);
      setNestedData({});
      setOrderList([]);
      setSelectedRowKeys([]);
      setExpandedRowKeys([]);
      setIsConfirmPrompts(false);
      newOrdersAdminV2({
        variables: {
          filter: {
            ...initialNewDeliveryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            orderType: ORDER_TYPE?.NEW,
            orderIds: finalWhereFilters?.orderNumber,
            search: orderSearchTerm,
            deliveryType: finalWhereFilters?.deliveryType,
            orderTypes:
              finalWhereFilters?.orderTypes?.length > 0
                ? finalWhereFilters?.orderTypes
                : undefined,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            deliveredOn: {
              from: finalWhereFilters?.deliveredOn?.from?.$d,
              to: finalWhereFilters?.deliveredOn?.to?.$d,
            },
            ...(finalWhereFilters?.expectedDeliveryDate?.from
              ? {
                  expectedDeliveryDate: {
                    from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                    to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                  },
                }
              : {}),
            statusTiming: finalWhereFilters?.statusTiming,
            pincodeIds: finalWhereFilters?.pincodes,
            libraryIds: finalWhereFilters?.name,
            city: finalWhereFilters?.city,
            isPreOrder: finalWhereFilters?.isPreOrder,
            isDelayed: finalWhereFilters?.isDelayed,
            orderTagIds:
              selectedTagIdsRef?.current?.length > 0
                ? selectedTagIdsRef.current
                : undefined,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
    setIsReadyLoading(false);
    setIsDelayReasonPrompts(false);
    delayForm?.resetFields();
  };

  const handleAddDelayReason = async (values) => {
    setIsReadyLoading(true);
    const metadataResponse = await updateOrderMetadata({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          metadata: [
            { key: 'delayReason', value: values?.reason },
            { key: 'delayNote', value: values?.comment },
          ],
        },
      },
    });
    if (metadataResponse?.data) {
      tempComment.current = {
        [order?.id]: {
          delayReason: { value: values?.reason },
          delayNote: { value: values?.comment },
        },
      };
      if (isOrderAction) {
        handleReadyOrder([order?.id]);
      } else {
        handleFulfillOrderItem();
      }
    } else {
      setIsReadyLoading(false);
    }
  };

  const handleCancel = () => {
    setIsContentMasterModalOpen(false);
    setOrderItem({});
  };

  const handleOrderChange = (value, record) => {
    setOrder(record);
    setIsOrderAction(true);
    if (value === FULFILLMENT_STATUS?.FULFILLED?.value) {
      setIsReadyPrompts(true);
    } else {
      setIsAddCommentModalOpen(true);
    }
  };

  const handleOrderItemChange = (value, record, orderDetails) => {
    setOrderItem(record);
    setOrder(orderDetails);
    setIsOrderAction(false);
    if (value === FULFILLMENT_STATUS?.FULFILLED?.value) {
      if (isUnitsFeatureEnabledForLibrary(orderDetails?.library?.id)) {
        setIsUnitScanOpen(true);
      } else {
        setIsFulfillPrompts(true);
      }
    } else {
      setIsAddCommentModalOpen(true);
    }
  };
  const renderBlinkingDotEl = (orderStatus) => {
    if (orderStatus === ORDER_STATUS.PLACED.value) {
      return (
        <Badge status="processing" color="red" className="custom-badge mr-8" />
      );
    }
    return null;
  };

  const renderSLA = (record) => {
    if (record?.nextStatusTime) {
      return (
        <div>{formatDate(record?.nextStatusTime, DATE_FORMATS?.WITH_TIME)}</div>
      );
    }
    return <div>N/A</div>;
  };

  const columns = [
    {
      title: 'ORDER ID',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'left',
      ellipsis: true,
      fixed: 'left',
      className: 'max-width-column',
      sorter: true,
      width: 150,
      sortOrder: sortedInfo?.columnKey === 'createdAt' && sortedInfo?.order,
      render: (_, record) => (
        <div className="d-flex align-center">
          {renderBlinkingDotEl(record?.order?.status)}
          <RenderCopyIcon copyText={record?.order?.orderNumber} />
          {record?.order?.orderNumber || '-'}
          <div onClick={(e) => e?.stopPropagation()}>
            {record?.order?.isExpressDelivery &&
              !record?.order?.isQuickDelivery && (
                <Tooltip
                  title={EXPRESS_DELIVERY_TOOLTIP_TEXT}
                  placement="top"
                  zIndex={4}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Tag
                    color="red"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="ml-8 mr-0 express-delivery-icon"
                  >
                    {EXPRESS_DELIVERY_EMOJI}
                  </Tag>
                </Tooltip>
              )}
            {record?.order?.isQuickDelivery && (
              <Tooltip
                title={QUICK_DELIVERY_TOOLTIP_TEXT}
                placement="top"
                zIndex={4}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Tag color="yellow" className="ml-8 mr-0 quick-delivery-icon">
                  {QUICK_DELIVERY_EMOJI}
                </Tag>
              </Tooltip>
            )}
          </div>
          {record?.order?.isFirstOrder && (
            <StatusFlag type={ORDER_TAG_TYPES.FIRST_ORDER} />
          )}
          {record?.order?.delayedInConfirmed && (
            <StatusFlag type={ORDER_TAG_TYPES.DELAYED_CONFIRMED} />
          )}
          {record?.order?.metadata?.isContainBigToy && <BigToyIcon />}
          {record?.isPreOrder && (
            <StatusFlag type={ORDER_TAG_TYPES.PRE_ORDER} />
          )}
          <OrderDynamicTags tags={record?.order?.orderTags} />
        </div>
      ),
    },
    {
      title: 'PRODUCT NAME',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
      align: 'left',
      width: 250,
      fixed: 'left',
      className: 'max-width-column',
      onCell: (record) => ({
        onClick: () =>
          isToyPermission && record?.toy?.id && openToyDrawer(record?.toy?.id),
        className: `${
          isToyPermission && record?.toy?.id && 'pointer table-cell'
        }`,
      }),
      render: (_, record) => (
        <>
          <div className="table-data-with-id d-flex align-center">
            {record?.toy?.name || '-'}
          </div>
          {record?.toy?.toyNumber && (
            <div className="font-size-12">({record?.toy?.toyNumber})</div>
          )}
        </>
      ),
    },
    {
      title: 'MAPPED UNIT',
      dataIndex: 'inventoryUnits',
      key: 'inventoryUnits',
      width: 200,
      align: 'left',
      className: 'max-width-column',
      onCell: () => ({
        onClick: (e) => e?.stopPropagation(),
      }),
      render: (_, record) => <MappedUnitsCell units={record?.inventoryUnits} />,
    },
    {
      title: 'IMAGES',
      dataIndex: 'images',
      key: 'images',
      ellipsis: true,
      width: 100,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => {
        const images = record?.toy?.images;
        return (
          <span>
            {images?.length > 0 ? (
              <>
                <Image
                  preview={{
                    visible: false,
                  }}
                  width={20}
                  height={20}
                  src={images?.[0]?.url}
                  onClick={(e) => {
                    e?.stopPropagation();
                    setVisible(true);
                    setToyImages(images);
                  }}
                  className="pointer"
                  keepRatio
                />
              </>
            ) : (
              <span>No Images</span>
            )}
            {images?.length > 1 ? ` + ${images?.length - 1}` : ''}
          </span>
        );
      },
    },
    {
      title: 'BRAND',
      dataIndex: 'brand',
      key: 'brand',
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) => record?.toy?.brand?.name || '-',
    },
    {
      title: 'QUANTITY',
      dataIndex: 'quantity',
      key: 'quantity',
      ellipsis: true,
      align: 'left',
      width: 100,
      className: 'max-width-column',
      render: (_, record) => record?.quantity || '-',
    },
    {
      title: 'STOCK AT BOOKING',
      dataIndex: 'stockAtBooking',
      key: 'stockAtBooking',
      align: 'left',
      width: 150,
      render: (_, record) => record?.stockAtBooking ?? '-',
    },
    {
      title: (
        <ColumnTitleWithInfo
          title="AVAILABLE FOR BOOKING"
          info={STOCK_COLUMN_TOOLTIPS.AVAILABLE_FOR_BOOKING}
        />
      ),
      dataIndex: 'availableStock',
      key: 'availableStock',
      align: 'left',
      width: 220,
      render: (_, record) => record?.libraryInventory?.availableStock ?? '-',
    },
    {
      title: (
        <ColumnTitleWithInfo
          title="PHYSICALLY AVAILABLE STOCK"
          info={STOCK_COLUMN_TOOLTIPS.PHYSICALLY_AVAILABLE_STOCK}
        />
      ),
      dataIndex: 'physicallyAvailableStock',
      key: 'physicallyAvailableStock',
      align: 'left',
      ellipsis: true,
      className: 'max-width-column',
      width: 260,
      render: (_, record) =>
        record?.libraryInventory?.physicallyAvailableStock ?? '-',
    },
    {
      title: (
        <ColumnTitleWithInfo
          title="BLOCKED UNITS"
          info={STOCK_COLUMN_TOOLTIPS.BLOCKED_UNITS}
        />
      ),
      dataIndex: 'pendingDispatchCount',
      key: 'pendingDispatchCount',
      align: 'left',
      ellipsis: true,
      className: 'max-width-column',
      width: 260,
      render: (_, record) =>
        record?.libraryInventory?.pendingDispatchCount ?? '-',
    },
    {
      title: 'PACKAGING MATERIAL',
      dataIndex: 'packagingMaterial',
      key: 'packagingMaterial',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <>
          {record?.toy?.bag ? (
            <div>
              <span className="mr-8">
                {record?.toy?.bag?.name} ({record?.toy?.bag?.size})
              </span>
              <Image
                preview={{ visible: false }}
                width={22}
                src={record?.toy?.bag?.image}
                onClick={(e) => {
                  e?.stopPropagation();
                  setPreviewVisible(true);
                  setToyPreviewImage(record?.toy?.bag?.image);
                }}
                className="pointer"
              />
            </div>
          ) : (
            '-'
          )}
        </>
      ),
    },

    {
      title: 'TOTAL ORDERS COUNT',
      dataIndex: 'totalOrdersCount',
      key: 'totalOrdersCount',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === 'totalOrdersCount' && sortedInfo?.order,
      render: (_, record) => record?.order?.totalOrdersCount ?? '-',
    },
    {
      title: 'ORDER STATUS',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      ellipsis: true,
      width: 180,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => {
        const status = record?.order?.status;
        if (!status) return '-';
        const config = ORDER_STATUS?.[status];
        return (
          <Badge color={config?.color} text={config?.text || humanizeEnum(status)} />
        );
      },
    },
    {
      title: 'ORDER TYPE',
      dataIndex: 'orderType',
      key: 'orderType',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) => record?.order?.orderType || '-',
      ...filterPopup('orderTypes', false),
    },
    {
      title: 'ORDER ITEM STATUS',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      width: 220,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => {
        const config =
          FULFILLMENT_STATUS?.[record?.status] ||
          ORDER_STATUS?.[record?.status];
        return config ? (
          <Badge color={config?.color} text={config?.text || config?.label} />
        ) : (
          '-'
        );
      },
    },
    {
      title: 'ORDER PACKAGING SLA',
      dataIndex: 'orderPackagingSLA',
      key: 'orderPackagingSLA',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) => <div>{renderSLA(record?.order)}</div>,
    },
    {
      title: 'ORDER PACKAGING TIME LEFT',
      dataIndex: 'timeLeft',
      key: 'timeLeft',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) => {
        const nextStatusTime = new Date(
          record?.order?.nextStatusTime,
        )?.getTime();
        return (
          <div>
            {!isEmpty(record?.order?.nextStatusTime) ? (
              <Countdown date={nextStatusTime} renderer={renderer} />
            ) : (
              'N/A'
            )}
          </div>
        );
      },
    },
    {
      title: 'DELIVERY TYPE',
      dataIndex: 'deliveryType',
      key: 'deliveryType',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) =>
        DELIVERY_TYPE?.[record?.order?.deliveryType]?.label || '-',
      ...filterPopup('deliveryType'),
    },
    {
      title: 'USER NAME',
      dataIndex: 'userName',
      key: 'userName',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: `max-width-column`,
      onCell: (record) => ({
        onClick: (e) =>
          isUserPermission &&
          record?.order?.user?.id &&
          handleOpenUserDrawer(e, record?.order?.user, dispatch),
        className: `${
          isUserPermission && record?.order?.user?.id && 'pointer table-cell'
        }`,
      }),
      render: (_, record) => {
        const userData = record?.order?.user;
        return (
          <>
            {userData?.firstName || userData?.lastName ? (
              <div className="table-data-with-id">{`${
                userData?.firstName ? userData?.firstName : ''
              } ${userData?.lastName ? userData?.lastName : ''}`}</div>
            ) : (
              '-'
            )}
            {userData?.userNumber ? (
              <div className="font-size-12">({userData?.userNumber})</div>
            ) : (
              ''
            )}
          </>
        );
      },
    },
    {
      title: 'LIBRARY NAME',
      dataIndex: 'name',
      key: 'name',
      width: 160,
      align: 'left',
      ellipsis: true,
      className: `max-width-column`,
      onCell: (record) => ({
        onClick: () => {
          const library = record?.order?.library;
          return (
            isLibraryPermission &&
            library?.id &&
            history?.push(
              `${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}${ROUTES?.BASIC_DETAILS}/view/${library?.id}/${library?.libraryNumber}`,
            )
          );
        },
        className: `${
          isLibraryPermission &&
          record?.order?.library?.id &&
          'pointer table-cell'
        }`,
      }),
      render: (_, record) => (
        <>
          <div className="table-data-with-id">
            {record?.order?.library?.name}
          </div>
          <div className="font-size-12">
            ({record?.order?.library?.libraryNumber})
          </div>
        </>
      ),
      ...filterPopup('name'),
    },
    {
      title: 'CITY',
      dataIndex: 'city',
      key: 'city',
      width: 160,
      align: 'left',
      ellipsis: true,
      className: `max-width-column`,
      render: (_, record) => (
        <>
          <div>{record?.order?.library?.city || '-'}</div>
        </>
      ),
      ...filterPopup('city'),
    },
    {
      title: 'PINCODE',
      dataIndex: 'pincode',
      key: 'pincode',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) => record?.order?.pincode?.pincode || '-',
      ...filterPopup('pincodes'),
    },
    {
      title: 'DELAY REASON',
      dataIndex: 'delayReason',
      key: 'delayReason',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <div className="action-button" onClick={(e) => e?.stopPropagation()}>
          {!isEmpty(record?.order?.metadata?.delayReason) ? (
            <Tooltip title="View reason" placement="top" zIndex={4}>
              <CommentIcon
                className="mr-16 pointer svg-icon"
                onClick={() => {
                  setIsReasonPrompt(true);
                  setOrder(record?.order);
                  setIsOrderAction(true);
                }}
              />
            </Tooltip>
          ) : (
            <span className="mr-24">-</span>
          )}
        </div>
      ),
    },
    {
      title: 'PLACED ON',
      dataIndex: 'placedOn',
      key: 'placedOn',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.order?.createdAt, DATE_FORMATS?.WITH_TIME),
      ...filterPopup('createdAt'),
    },
    {
      title: 'ESTIMATED SHIPPING DATE',
      dataIndex: 'deliveredOn',
      key: 'deliveredOn',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.order?.deliveredOn, DATE_FORMATS?.WITH_TIME) || '-',
      ...filterPopup('deliveredOn'),
    },
    {
      title: 'EXPECTED DELIVERY DATE',
      dataIndex: 'expectedDeliveryDate',
      key: 'expectedDeliveryDate',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === 'expectedDeliveryDate' && sortedInfo?.order,
      render: (_, record) =>
        formatDate(
          record?.order?.expectedDeliveryDate,
          DATE_FORMATS?.WITH_TIME,
        ) || '-',
      ...filterPopup('expectedDeliveryDate'),
    },
    {
      title: 'FULFILMENT POSSIBILITY',
      dataIndex: 'fulfilmentPossibility',
      key: 'fulfilmentPossibility',
      align: 'left',
      width: 200,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedPossibilityItem(record);
            setPossibilityDrawerVisible(true);
          }}
        >
          View
        </Button>
      ),
    },
    ...getStaffColumns(
      (record) => record?.order?.library?.id,
      !orderList?.some((record) =>
        isUnitsFeatureEnabledForLibrary(record?.order?.library?.id),
      ),
    ),
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      width: 250,
      render: (_, record) => {
        const options = [
          {
            label: 'Mark as fulfilled',
            value: 'MARK_AS_FULFILLED',
            disabled:
              record?.isPreOrder &&
              record?.libraryInventory?.availableStock <= 0,
          },
          { label: 'Mark as cancelled', value: 'MARK_AS_CANCELLED' },
        ];
        let selectValue = null;
        if (record?.order?.status === ORDER_STATUS?.CANCELLED?.value) {
          selectValue = 'MARK_AS_CANCELLED';
        } else if (record?.status === FULFILLMENT_STATUS?.FULFILLED?.value) {
          selectValue = 'MARK_AS_FULFILLED';
        }

        return (
          <div onClick={(e) => e?.stopPropagation()}>
            <SelectComponent
              size="small"
              className="full-width"
              placeholder="Select Action"
              showSearch={false}
              value={selectValue}
              disabled={
                record?.order?.status === ORDER_STATUS?.CONFIRMED?.value ||
                record?.order?.status === ORDER_STATUS?.CANCELLED?.value ||
                record?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
                record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
                record?.status === FULFILLMENT_STATUS?.CANCELLED?.value ||
                (record?.transferOrders?.length > 0 &&
                  record?.transferOrders?.[0]?.status !==
                    TRANSFER_ORDER_STATUS.RECEIVED.value)
              }
              record={record}
              options={options}
              onChange={(value) => {
                if (value === 'MARK_AS_CANCELLED') {
                  handleOrderChange('UNFULFILLED', record?.order);
                } else if (value === 'MARK_AS_FULFILLED') {
                  handleOrderItemChange('FULFILLED', record, record?.order);
                }
              }}
              allowClear={false}
            />
          </div>
        );
      },
    },
  ]?.filter((item) => !item?.hidden);

  const locale = {
    emptyText: isEmptyOrderList ? '' : <span />,
  };

  const selectedOrderIds = useMemo(() => {
    const ids = (selectedRowKeys || [])
      .map((itemId) => selectedOrdersById?.[itemId]?.order?.id)
      .filter(Boolean);
    return Array.from(new Set(ids));
  }, [selectedRowKeys, selectedOrdersById]);

  const selectedOrdersUniqueTags = useMemo(() => {
    const selectedOrders = (selectedRowKeys || [])
      .map((id) => selectedOrdersById?.[id])
      .filter(Boolean);

    return Array.from(
      new Map(
        selectedOrders
          ?.flatMap((o) => o?.order?.orderTags || [])
          ?.filter((t) => t?.id)
          ?.map((t) => [t?.id, t]),
      ).values(),
    );
  }, [selectedOrdersById, selectedRowKeys]);

  const onSelectChange = (newSelectedRowKeys, selectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);

    setSelectedOrdersById((prev) => {
      const next = { ...prev };
      const currentPageOrderIds = (orderList || [])?.map((o) => o?.id);

      // Remove unchecked orders from currently visible page.
      currentPageOrderIds?.forEach((id) => {
        if (id && !newSelectedRowKeys?.includes(id)) {
          delete next[id];
        }
      });

      // Cache selected order details from currently visible rows.
      (selectedRows || [])?.forEach((selectedOrder) => {
        if (selectedOrder?.id) {
          next[selectedOrder?.id] = selectedOrder;
        }
      });

      return next;
    });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
    preserveSelectedRowKeys: true,
  };

  const staffLibraryIds = uniq(
    (selectedRowKeys || [])
      ?.map((itemId) => selectedOrdersById?.[itemId]?.order?.library?.id)
      ?.filter(Boolean),
  );

  const canBulkAssignStaff =
    selectedRowKeys?.length > 0 &&
    (selectedRowKeys || [])?.every((itemId) =>
      isUnitsFeatureEnabledForLibrary(
        selectedOrdersById?.[itemId]?.order?.library?.id,
      ),
    );

  const getSelectedOrderItems = async () =>
    (selectedRowKeys || [])
      ?.map((itemId) => selectedOrdersById?.[itemId])
      ?.filter(Boolean);

  const handleBulkStaffAssigned = (orderItemIds, field, user) => {
    setOrderList((prev) =>
      patchOrderItemStaff(prev, orderItemIds, field, user),
    );
    setSelectedRowKeys([]);
    setSelectedOrdersById({});
  };

  const handleFilter = (values) => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    paginationPropRef.current = {
      ...paginationPropRef?.current,
      current: 1,
      skip: 0,
    };

    setIsOrderLoading(true);
    setIsAllFilterModalOpen(false);
    setOrderList([]);
    setIsActive(true);
    const filters = {
      status: values?.status,
      createdAt: values?.createdAt?.[1]
        ? { from: values?.createdAt?.[0], to: values?.createdAt?.[1] }
        : null,
      deliveredOn: values?.deliveredOn?.[1]
        ? { from: values?.deliveredOn?.[0], to: values?.deliveredOn?.[1] }
        : null,
      pincodes: values?.pincodes,
      name: values?.name,
      deliveryType: values?.deliveryType,
      orderTypes: values?.orderTypes,
      city: values?.city,
    };
    setWhereFilter(filters);
    setFinalWhereFilters(filters);
    finalWhereFiltersRef.current = filters;
    saveRetainedFilterState(tableFilterKey, {
      filters,
      paginationProp: { ...paginationProp, skip: 0, current: 1 },
    });
    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          search: orderSearchTerm,
          deliveryType: values?.deliveryType,
          orderTypes:
            values?.orderTypes?.length > 0 ? values?.orderTypes : undefined,
          status: values?.status?.length > 0 ? values?.status : statusFilter,
          dateRange: {
            from: values?.createdAt?.[0],
            to: values?.createdAt?.[1],
          },
          deliveredOn: {
            from: values?.deliveredOn?.[0],
            to: values?.deliveredOn?.[1],
          },
          pincodeIds: values?.pincodes,
          libraryIds: values?.name,
          city: values?.city,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  const handleSelectAllMobile = (checked) => {
    if (checked) {
      setSelectedRowKeys((prev) => {
        const newIds = (orderList || [])?.map((data) => data?.id);
        const mergedIds = Array.from(new Set([...prev, ...newIds]));

        setSelectedOrdersById((prevMap) => {
          const nextMap = { ...prevMap };
          (orderList || [])?.forEach((orderData) => {
            if (orderData?.id) {
              nextMap[orderData.id] = orderData;
            }
          });
          return nextMap;
        });

        return mergedIds;
      });
    } else {
      setSelectedRowKeys([]);
      setSelectedOrdersById({});
    }
  };

  const handleSelectOrders = (key) => {
    setSelectedRowKeys((prev) => {
      const selectedOrder = (orderList || [])?.find((item) => item?.id === key);
      if (prev?.includes(key)) {
        setSelectedOrdersById((prevMap) => {
          const nextMap = { ...prevMap };
          delete nextMap[key];
          return nextMap;
        });
        return prev?.filter((item) => item !== key);
      }

      if (selectedOrder?.id) {
        setSelectedOrdersById((prevMap) => ({
          ...prevMap,
          [selectedOrder.id]: selectedOrder,
        }));
      }

      return [...prev, key];
    });
  };

  const findSelectableOrders = () => orderList || [];

  return (
    <>
      <ContentMasterModal
        isModalOpen={isContentMasterModalOpen}
        contentHistory={orderItem?.contentHistory}
        handleCancel={handleCancel}
      />
      <DelayReasonModal
        note={
          !isEmpty(order?.metadata?.delayReason)
            ? order?.metadata
            : tempComment?.current?.[order?.id]
        }
        isModalOpen={isReasonPrompt}
        setIsModalOpen={setIsReasonPrompt}
        isOrderAction={isOrderAction}
        adminNote={
          isOrderAction ? order?.comment?.comment : orderItem?.comment?.comment
        }
      />
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isFulfillPrompts}
        onOk={() => {
          if (
            order?.delayedInConfirmed &&
            isEmpty(order?.metadata?.delayReason)
          ) {
            setIsFulfillPrompts(false);
            setIsDelayReasonPrompts(true);
          } else {
            setIsFulfillPrompts(false);
            handleFulfillOrderItem();
          }
        }}
        onCancel={() => setIsFulfillPrompts(false)}
        okButtonProps={{ loading: isFulfillLoading }}
      >
        Are you sure you want to
        <strong> Mark as fulfill </strong>
        this order item?
      </Modal>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isReadyPrompts}
        onOk={() => {
          if (
            order?.delayedInConfirmed &&
            isEmpty(order?.metadata?.delayReason)
          ) {
            setIsReadyPrompts(false);
            setIsDelayReasonPrompts(true);
          } else {
            handleReadyOrder([order?.id]);
            setIsReadyPrompts(false);
          }
        }}
        onCancel={() => setIsReadyPrompts(false)}
        okButtonProps={{ loading: isReadyLoading }}
      >
        Are you sure you want to
        <strong> Mark as ready </strong>
        this order?
      </Modal>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isConfirmPrompts}
        onOk={() => handleReadyOrder(selectedRowKeys)}
        onCancel={() => setIsConfirmPrompts(false)}
        okButtonProps={{ loading: isReadyLoading }}
      >
        Are you sure you want to
        <strong> Confirm </strong>
        this orders?
      </Modal>
      {isDelayReasonPrompts && (
        <AddDelayReasonModal
          form={delayForm}
          isOrderAction={isOrderAction}
          isModalOpen={isDelayReasonPrompts}
          setIsModalOpen={setIsDelayReasonPrompts}
          isBtnLoading={isReadyLoading}
          onFinish={handleAddDelayReason}
        />
      )}
      <UnitScanModal
        open={isUnitScanOpen}
        mode={UNIT_SCAN_MODES?.PICK}
        orderItemId={orderItem?.id}
        toyName={orderItem?.toy?.name}
        onClose={() => setIsUnitScanOpen(false)}
        onSuccess={(data) => {
          if (data?.isOrderItemFulfilled) {
            fetchOrders();
          }
        }}
      />
      <AddCommentModal
        form={form}
        isOrderAction={isOrderAction}
        isModalOpen={isAddCommentModalOpen}
        setIsModalOpen={setIsAddCommentModalOpen}
        isBtnLoading={isAddCommentLoading}
        onFinish={
          isOrderAction ? handleAddCommentInOrder : handleAddCommentInOrderItem
        }
      />
      <CommonPreviewTable
        visible={visible}
        setVisible={setVisible}
        images={toyImages}
        setImages={setToyImages}
      />
      <CommonPreview
        visible={previewVisible}
        setVisible={setPreviewVisible}
        image={toyPreviewImage}
        setImage={setToyPreviewImage}
      />
      <NoteModal
        note={
          isOrderAction ? order?.cancellationData : orderItem?.cancellationData
        }
        isModalOpen={isNotePrompts}
        setIsModalOpen={setIsNotePrompts}
        isOrderAction={isOrderAction}
        adminNote={
          isOrderAction ? order?.comment?.comment : orderItem?.comment?.comment
        }
      />
      <FulfilmentPossibilityDrawer
        visible={possibilityDrawerVisible}
        onClose={() => setPossibilityDrawerVisible(false)}
        libraryInventoryId={selectedPossibilityItem?.libraryInventory?.id}
        physicallyAvailableStock={
          selectedPossibilityItem?.libraryInventory?.physicallyAvailableStock
        }
        isDesktop={isDesktop}
      />
      {!isDesktop && (
        <ShowDelayedOrdersSwitch
          finalWhereFiltersRef={finalWhereFiltersRef}
          finalWhereFilters={finalWhereFilters}
          setFinalWhereFilters={setFinalWhereFilters}
          setPaginationProp={setPaginationProp}
          paginationPropRef={paginationPropRef}
          setOrderList={setOrderList}
          fetchOrders={fetchOrders}
          paginationProp={paginationProp}
          setIsOrderLoading={setIsOrderLoading}
          tableFilterKey={tableFilterKey}
          margin
          setSortedInfo={setSortedInfo}
          sortedInfoRef={sortedInfoRef}
          sortedInfo={sortedInfo}
        />
      )}
      <div className="d-flex justify-end align-center mb-16">
        {isDesktop && (
          <ShowDelayedOrdersSwitch
            finalWhereFiltersRef={finalWhereFiltersRef}
            finalWhereFilters={finalWhereFilters}
            setFinalWhereFilters={setFinalWhereFilters}
            setPaginationProp={setPaginationProp}
            paginationPropRef={paginationPropRef}
            setOrderList={setOrderList}
            fetchOrders={fetchOrders}
            paginationProp={paginationProp}
            setIsOrderLoading={setIsOrderLoading}
            tableFilterKey={tableFilterKey}
            setSortedInfo={setSortedInfo}
            sortedInfoRef={sortedInfoRef}
            sortedInfo={sortedInfo}
          />
        )}
        <OrderStatusSelect
          finalWhereFiltersRef={finalWhereFiltersRef}
          finalWhereFilters={finalWhereFilters}
          setFinalWhereFilters={setFinalWhereFilters}
          fetchOrders={() => {
            setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
            paginationPropRef.current = {
              ...paginationPropRef?.current,
              current: 1,
              skip: 0,
            };
            setOrderList([]);
            fetchOrders();
          }}
          setIsOrderLoading={setIsOrderLoading}
          tableFilterKey={tableFilterKey}
        />
        <BulkActionButton
          selectedItemIds={selectedOrderIds}
          actions={ORDER_TAG_BULK_ACTIONS}
          scope="SYSTEM_USERS_END_USERS"
          supportsNestedSubmit
          selectedTagsForRemove={selectedOrdersUniqueTags}
          drawerTitle="Order Bulk Actions"
          className="mr-8"
          onSuccess={() => {
            setOrderList([]);
            fetchOrders();
          }}
          onClearSelection={() => {
            setSelectedRowKeys([]);
            setSelectedOrdersById({});
          }}
          tagConfigSubtype="ORDERS"
          useOrderTagsApi
          orderTagTargetField="orders"
        />
        {canBulkAssignStaff && (
          <BulkAssignStaffButton
            hasSelection={selectedRowKeys?.length > 0}
            libraryIds={staffLibraryIds}
            getOrderItems={getSelectedOrderItems}
            onSuccess={handleBulkStaffAssigned}
          />
        )}
        {!isDesktop && (
          <Badge dot={hasNonEmptyValueObj(finalWhereFilters)} size="small">
            <FilterIcon
              className="mobile-svg-icon ml-16"
              onClick={() => setIsAllFilterModalOpen(true)}
            />
          </Badge>
        )}
        <OrderTagsFilter
          value={selectedTagIds}
          onChange={handleTagFilterChange}
          placeholder="Filter by Tags"
          className="ml-8"
        />
        {isDesktop && (
          <SearchComponent
            className="search-box ml-16"
            query={orderSearchTerm}
            setQuery={setOrderSearchTerm}
            tableFilterKey={tableFilterKey}
            orderSearchTermRef={orderSearchTermRef}
            getData={handleSearch}
          />
        )}
      </div>

      {!isDesktop && (
        <div className="d-flex justify-end align-center">
          <SearchComponent
            className="search-box"
            query={orderSearchTerm}
            setQuery={setOrderSearchTerm}
            orderSearchTermRef={orderSearchTermRef}
            getData={handleSearch}
          />
          <Checkbox
            indeterminate={
              selectedRowKeys?.length < findSelectableOrders()?.length &&
              selectedRowKeys?.length > 0
            }
            checked={
              selectedRowKeys?.length > 0 &&
              selectedRowKeys?.length === findSelectableOrders()?.length &&
              !isOrderLoading &&
              !isFetchMoreOrderLoading
            }
            onChange={(e) => {
              handleSelectAllMobile(e?.target?.checked);
            }}
            className="ml-16"
          >
            Select all
          </Checkbox>

          <div className="ml-16 d-flex align-center">
            <SortDropdown
              isOpen={isAllSortPopoverOpen}
              setIsOpen={setIsAllSortPopoverOpen}
              options={map(sort, (item) => item)}
              sortedInfo={sortedInfo}
              setSortedInfo={setSortedInfo}
              sortedInfoRef={sortedInfoRef}
              onFinish={handleSort}
              tableFilterKey={tableFilterKey}
            />
          </div>
        </div>
      )}
      {isDesktop ? (
        <div className="colored-table">
          <CommonTable
            rowClassName={(record) =>
              getRowClassName(
                record?.order,
                thresholdData,
                playNotificationSound,
                criticalOrdersRef,
              )
            }
            locale={locale}
            columns={columns}
            data={orderList || []}
            loading={isOrderLoading}
            rowSelection={rowSelection}
            onChange={handleTableChange}
            paginationConfig={paginationProp}
            rowKey={(record) => record?.id}
          />
        </div>
      ) : (
        <div className="mobile-container-search-action no-scroll-bar">
          <AllFilterOrderModal
            onFinish={handleFilter}
            isModalOpen={isAllFilterModalOpen}
            setIsModalOpen={setIsAllFilterModalOpen}
            finalWhereFilters={finalWhereFilters}
            setFinalWhereFilters={setFinalWhereFilters}
            isNewPath
            hideStatus
            isAssignDelPartnerTab={false}
            finalWhereFiltersRef={finalWhereFiltersRef}
            isAdmin
            tableFilterKey={tableFilterKey}
          />
          <AllOrderItemModal
            order={order}
            isNewPath
            setOrder={setOrder}
            isModalOpen={isAllOrderItemModalOpen}
            setIsModalOpen={setIsAllOrderItemModalOpen}
            handleExpand={handleExpand}
            nestedData={nestedData}
            isLoading={isLoading}
            setIsNotePrompts={setIsNotePrompts}
            setOrderItem={setOrderItem}
            setIsOrderAction={setIsOrderAction}
            handleOrderItemChange={handleOrderItemChange}
            setIsContentMasterModalOpen={setIsContentMasterModalOpen}
            setVisible={setVisible}
            setToyImages={setToyImages}
            isAdmin
            isAssignDelPartnerTab={false}
          />
          <AllOrderList
            orderList={orderList}
            infiniteScrollRef={infiniteScrollRef}
            isFetchMoreOrderLoading={isFetchMoreOrderLoading}
            isEmptyOrderList={isEmptyOrderList}
            isOrderLoading={isOrderLoading}
            setOrder={setOrder}
            isNewPath
            setIsNotePrompts={setIsNotePrompts}
            setIsOrderAction={setIsOrderAction}
            handleOrderChange={handleOrderChange}
            setIsAllOrderItemModalOpen={setIsAllOrderItemModalOpen}
            setVisible={setVisible}
            setToyImages={setToyImages}
            handleSelectOrders={handleSelectOrders}
            selectedOrders={selectedRowKeys}
            setIsReasonPrompt={setIsReasonPrompt}
            isAdmin
            isAssignDelPartnerTab={false}
            setDeliveryPartnerData={() => {}}
            setIsDeliveryPartnerPrompts={() => {}}
            showBlink
            renderBlinkingDotEl={renderBlinkingDotEl}
          />
        </div>
      )}
    </>
  );
};

export default NewFulfilmentTable;
